import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../shared/lib/apiClient';
import { useAuth } from './context/AuthContext';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';

const ease = 'cubic-bezier(0.16,1,0.3,1)';

const industries = [
  {
    id: 'restaurant', label: 'Restaurant / Food Service',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></svg>,
  },
  {
    id: 'retail', label: 'Retail / E-Commerce',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>,
  },
  {
    id: 'professional_services', label: 'Professional Services',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
  },
  {
    id: 'construction', label: 'Construction / Trades',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 18v3h3" /><path d="M14 2.5V5l3.5 3.5" /><path d="m22 22-5.5-5.5" /><path d="m18 16 4 4" /><path d="M2 22 17.5 6.5" /></svg>,
  },
  {
    id: 'healthcare', label: 'Healthcare',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4" /><path d="M12 2v4" /><path d="M16 2v4" /><rect x="4" y="6" width="16" height="16" rx="2" /><path d="M10 12h4" /><path d="M12 10v4" /></svg>,
  },
  {
    id: 'manufacturing', label: 'Manufacturing / Distribution',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" /></svg>,
  },
];

const Onboarding = () => {
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'join'
  const navigate = useNavigate();
  const { fetchCompanies } = useAuth();

  // Create company
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [otherIndustry, setOtherIndustry] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Join company
  const [inviteCode, setInviteCode] = useState('');
  const [resolvedInvite, setResolvedInvite] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joinSuccess, setJoinSuccess] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) { setError('Company name is required.'); return; }

    setError('');
    setCreating(true);

    try {
      const industryValue = industry === 'other' ? (otherIndustry.trim() || 'Other') : (industry || null);
      const response = await apiFetch('/api/companies', {
        method: 'POST',
        body: JSON.stringify({
          name: companyName.trim(),
          industry: industryValue,
        }),
      });
      const data = await response.json();
      if (!response.ok) { setError(data.error || 'Failed to create company'); setCreating(false); return; }
      if (data.company?.id) localStorage.setItem('active_company_id', data.company.id);
      await fetchCompanies();
      navigate('/dashboard');
    } catch {
      setError('Failed to create company. Please try again.');
      setCreating(false);
    }
  };

  const handleResolveInvite = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) { setError('Please enter an invite code.'); return; }
    setError('');
    setJoining(true);

    try {
      const response = await apiFetch(`/api/invites/resolve/${encodeURIComponent(inviteCode.trim())}`);
      const data = await response.json();
      if (!response.ok) { setError(data.error || 'Invalid invite code'); setJoining(false); return; }
      setResolvedInvite(data);
      setJoining(false);
    } catch {
      setError('Failed to validate invite code.');
      setJoining(false);
    }
  };

  const handleJoin = async () => {
    setError('');
    setJoining(true);

    try {
      const response = await apiFetch('/api/invites/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: inviteCode.trim() }),
      });
      const data = await response.json();
      if (!response.ok) { setError(data.error || 'Failed to join company'); setJoining(false); return; }
      if (data.companyId) localStorage.setItem('active_company_id', data.companyId);
      await fetchCompanies();
      setJoinSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch {
      setError('Failed to join company.');
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header
        className="px-6 sm:px-10 py-6 flex items-center justify-between"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(-8px)',
          transition: `all 0.8s ${ease}`,
        }}
      >
        <Link to="/">
          <img src={logo} alt="Arbcor" className="h-5 w-auto select-none" draggable="false" />
        </Link>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-[13px] text-zinc-400 hover:text-zinc-600 transition-colors"
        >
          Skip for now
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 pt-8 sm:pt-16 pb-12">
        <div className="w-full max-w-md">

          <h1
            className="text-[24px] font-semibold text-zinc-900 mb-2"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(8px)',
              transition: `all 0.6s ${ease}`,
              transitionDelay: '150ms',
            }}
          >
            Set up your workspace
          </h1>
          <p
            className="text-[14px] text-zinc-400 mb-6"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(8px)',
              transition: `all 0.6s ${ease}`,
              transitionDelay: '220ms',
            }}
          >
            Create a new company or join an existing one
          </p>

          {/* Mode toggle */}
          <div
            className="flex items-center gap-1 p-1 bg-zinc-100 rounded-md mb-6"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(8px)',
              transition: `all 0.5s ${ease}`,
              transitionDelay: '280ms',
            }}
          >
            <button
              onClick={() => { setMode('create'); setError(''); setResolvedInvite(null); }}
              className={`flex-1 py-2 text-[13px] font-medium rounded transition-all ${
                mode === 'create' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              Create Company
            </button>
            <button
              onClick={() => { setMode('join'); setError(''); }}
              className={`flex-1 py-2 text-[13px] font-medium rounded transition-all ${
                mode === 'join' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              Join with Code
            </button>
          </div>

          {error && (
            <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-[12px] text-red-600">
              {error}
            </div>
          )}

          {/* ── CREATE MODE ── */}
          {mode === 'create' && (
            <form onSubmit={handleCreateCompany}>
              <div
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                  transition: `all 0.5s ${ease}`,
                  transitionDelay: '340ms',
                }}
              >
                <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1.5">Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => { setCompanyName(e.target.value); setError(''); }}
                  placeholder="Your company name"
                  autoFocus
                  disabled={creating}
                  className="w-full px-3.5 py-2.5 border border-zinc-200 text-[14px] text-zinc-900 placeholder-zinc-300 rounded-md outline-none focus:border-zinc-900 transition-colors disabled:opacity-50"
                />
              </div>

              {/* Industry */}
              <div
                className="mt-5"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                  transition: `all 0.5s ${ease}`,
                  transitionDelay: '400ms',
                }}
              >
                <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-2.5">Industry</label>
                <div className="grid grid-cols-2 gap-2">
                  {industries.map((ind) => (
                    <button
                      key={ind.id}
                      type="button"
                      onClick={() => { setIndustry(ind.id); setOtherIndustry(''); }}
                      disabled={creating}
                      className={`group relative p-3 border rounded-md text-left transition-all duration-200 disabled:opacity-50 ${
                        industry === ind.id
                          ? 'border-zinc-900 bg-zinc-900 text-white'
                          : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm text-zinc-700'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded flex items-center justify-center mb-2 transition-colors ${
                        industry === ind.id ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-400'
                      }`}>
                        {ind.icon}
                      </div>
                      <span className={`text-[12px] font-medium ${industry === ind.id ? 'text-white' : ''}`}>{ind.label}</span>
                    </button>
                  ))}
                </div>

                {/* Other industry */}
                <div className="mt-2">
                  {industry === 'other' ? (
                    <div className="border border-zinc-900 rounded-md overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900">
                        <span className="text-[12px] font-medium text-white">Other</span>
                        <button type="button" onClick={() => { setIndustry(''); setOtherIndustry(''); }} className="text-white/50 hover:text-white transition-colors">
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 4L4 12M4 4l8 8" /></svg>
                        </button>
                      </div>
                      <div className="px-3 py-2.5 bg-white">
                        <input
                          autoFocus
                          type="text"
                          value={otherIndustry}
                          onChange={(e) => setOtherIndustry(e.target.value)}
                          placeholder="Describe your industry..."
                          disabled={creating}
                          className="w-full text-[13px] text-zinc-900 placeholder-zinc-300 outline-none disabled:opacity-50"
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIndustry('other')}
                      disabled={creating}
                      className="w-full flex items-center justify-between px-3 py-2 border border-zinc-200 rounded-md text-[13px] text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 transition-all disabled:opacity-50"
                    >
                      Other industry
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                    </button>
                  )}
                </div>
              </div>

              <div
                className="mt-6"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                  transition: `all 0.5s ${ease}`,
                  transitionDelay: '460ms',
                }}
              >
                <button
                  type="submit"
                  disabled={creating || !companyName.trim()}
                  className="w-full px-4 py-3 bg-zinc-900 text-white text-[14px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create & Continue'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ── JOIN MODE ── */}
          {mode === 'join' && (
            <div
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                transition: `all 0.5s ${ease}`,
                transitionDelay: '340ms',
              }}
            >
              {joinSuccess ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="text-[16px] font-semibold text-zinc-900 mb-1">You're in!</h3>
                  <p className="text-[13px] text-zinc-400">Redirecting to your dashboard...</p>
                </div>
              ) : resolvedInvite ? (
                <div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md mb-4">
                    <p className="text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1">You're joining</p>
                    <p className="text-[16px] font-semibold text-zinc-900">{resolvedInvite.companyName}</p>
                    <p className="text-[12px] text-zinc-400 mt-0.5">as {resolvedInvite.role || 'member'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setResolvedInvite(null); setInviteCode(''); }}
                      className="flex-1 px-4 py-2.5 border border-zinc-200 text-[13px] font-medium text-zinc-600 rounded-md hover:bg-zinc-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleJoin}
                      disabled={joining}
                      className="flex-1 px-4 py-2.5 bg-zinc-900 text-white text-[13px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {joining ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Confirm & Join'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleResolveInvite}>
                  <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1.5">Invite Code</label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setError(''); }}
                    placeholder="Enter code"
                    autoFocus
                    disabled={joining}
                    className="w-full px-3.5 py-2.5 border border-zinc-200 text-[14px] text-zinc-900 placeholder-zinc-300 rounded-md outline-none focus:border-zinc-900 transition-colors font-mono tracking-wider disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={joining || !inviteCode.trim()}
                    className="w-full mt-4 px-4 py-3 bg-zinc-900 text-white text-[14px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {joining ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
